import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Video, VideoOff, PhoneOff, Sparkles, Wand2, ChevronRight, Wifi, WifiOff, Send, Volume2, VolumeX } from "lucide-react";
import { useAudioStream } from "../../hooks/useAudioStream";
import { formatMockReport } from "../../hooks/mockTestReport";
import { supabase } from "../../lib/supabase";
import PostSessionSummary from "../../components/PostSessionSummary";
import { SessionPlanCard } from "../../components/aura/SessionPlanCard";
import { getBackendUrl } from "../../lib/utils";
import { teil3ImageUrl } from "../../lib/assets";

// ─── Waveform ─────────────────────────────────────────────────────────────────

const wave = (seed: number, n: number) => {
  const out = [];
  let s = seed;
  for (let i = 0; i < n; i++) { s = (s * 9301 + 49297) % 233280; out.push(0.3 + (s / 233280) * 0.7); }
  return out;
};

const LiveWave = ({ active, tint = "cyan", bars = 56 }: { active: boolean, tint?: string, bars?: number }) => {
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf: number;
    const loop = () => { setT((v) => v + 0.09); raf = requestAnimationFrame(loop); };
    if (active) raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active]);
  const base = wave(tint === "cyan" ? 17 : 31, bars);
  return (
    <div className="flex items-end gap-[3px] h-12 w-full justify-center px-3">
      {base.map((a, i) => {
        const h = active ? 14 + Math.abs(Math.sin(t + i * 0.34)) * 32 * a : 6 + a * 4;
        return (
          <span key={i} className="block w-[3px] rounded-full"
            style={{
              height: `${h}px`,
              background: tint === "cyan"
                ? "linear-gradient(180deg, rgb(var(--accent-cyan-rgb)), rgb(var(--accent-blue-rgb)))"
                : "linear-gradient(180deg, rgb(var(--accent-violet-rgb)), rgb(var(--accent-blue-rgb)))",
              opacity: active ? 0.9 : 0.32,
              transition: "height 110ms ease, opacity 220ms ease",
            }}
          />
        );
      })}
    </div>
  );
};

// ─── Avatars ──────────────────────────────────────────────────────────────────

const AuraAvatar = ({ speaking }: { speaking: boolean }) => (
  <div className="relative w-36 h-36 sm:w-44 sm:h-44 grid place-items-center">
    <motion.div className="absolute inset-0 rounded-full"
      style={{ background: "radial-gradient(closest-side, rgb(var(--accent-cyan-rgb) / 0.55), transparent 70%)" }}
      animate={{ scale: speaking ? [1, 1.08, 1] : 1, opacity: speaking ? [0.55, 0.95, 0.55] : 0.4 }}
      transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div className="absolute inset-3 rounded-full border border-aura-cyan/30" animate={{ rotate: 360 }} transition={{ duration: 18, repeat: Infinity, ease: "linear" }} />
    <motion.div className="absolute inset-6 rounded-full border border-aura-cyan/20" animate={{ rotate: -360 }} transition={{ duration: 24, repeat: Infinity, ease: "linear" }} />
    <div className="relative w-20 h-20 rounded-full glass edge-glow grid place-items-center">
      <div className="w-3 h-3 rounded-full bg-aura-cyan shadow-[0_0_18px_rgb(var(--accent-cyan-rgb)/0.95)]" />
    </div>
  </div>
);

const YouAvatar = ({ speaking, initial }: { speaking: boolean; initial: string }) => (
  <div className="relative w-36 h-36 sm:w-44 sm:h-44 grid place-items-center">
    <motion.div className="absolute inset-0 rounded-full"
      style={{ background: "radial-gradient(closest-side, rgb(var(--accent-violet-rgb) / 0.45), transparent 70%)" }}
      animate={{ scale: speaking ? [1, 1.08, 1] : 1, opacity: speaking ? [0.4, 0.8, 0.4] : 0.3 }}
      transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
    />
    <div className="relative w-28 h-28 rounded-full glass hairline grid place-items-center overflow-hidden">
      <span className="font-display font-light text-[48px] text-ink tracking-tighter">{initial}</span>
    </div>
  </div>
);

// ─── Correction extraction ────────────────────────────────────────────────────
//
// Parses AURA's latest transcript for common correction patterns:
//   "we say X" / "should be X" / "try saying X" / "the correct form is X"
//   "instead of X, say Y" / "you should say X"
//
// Returns { was, now, rule } or null if no correction signal detected.

interface CorrectionData {
  was: string;
  now: string;
  rule: string;
}

function extractCorrection(transcripts: Array<{ role: string; text: string }>): CorrectionData | null {
  // Only look at the most recent AURA turn
  const auraTurns = transcripts.filter((t) => t.role === "aura");
  const last = auraTurns[auraTurns.length - 1];
  if (!last?.text) return null;

  const text = last.text;

  // Pattern: "instead of [X], say/use [Y]" or "instead of [X] you should say [Y]"
  const insteadOf = text.match(/instead of\s+["""]?(.+?)["""]?,\s*(?:say|use|try)\s+["""]?(.+?)["""]?[.,!]/i);
  if (insteadOf) {
    return { was: insteadOf[1].trim(), now: insteadOf[2].trim(), rule: "Correction" };
  }

  // Pattern: "should be [Y], not [X]" or "should be [Y]"
  const shouldBe = text.match(/should be\s+["""]?(.+?)["""]?(?:,\s*not\s+["""]?(.+?)["""]?)?[.,!]/i);
  if (shouldBe) {
    return { was: shouldBe[2]?.trim() || "…", now: shouldBe[1].trim(), rule: "Grammar" };
  }

  // Pattern: "we say [Y]" / "we would say [Y]"
  const weSay = text.match(/we (?:would )?say\s+["""]?(.+?)["""]?[.,!]/i);
  if (weSay) {
    return { was: "…", now: weSay[1].trim(), rule: "Native form" };
  }

  // Pattern: "the correct [\w]+ is [Y]"
  const correct = text.match(/the correct \w+ is\s+["""]?(.+?)["""]?[.,!]/i);
  if (correct) {
    return { was: "…", now: correct[1].trim(), rule: "Correction" };
  }

  // Pattern: "try saying [Y]"
  const trySaying = text.match(/try saying\s+["""]?(.+?)["""]?[.,!]/i);
  if (trySaying) {
    return { was: "…", now: trySaying[1].trim(), rule: "Suggestion" };
  }

  return null;
}

const memoryTermKey = (item: any) => String(item?.term || item || '').trim().toLowerCase();
const memoryFactKey = (item: any) => String(item?.fact || item || '').trim().toLowerCase();

// ─── LiveSession ──────────────────────────────────────────────────────────────

const LiveSession = () => {
  const nav = useNavigate();
  const [muted, setMuted] = useState(false);
  const [video, setVideo] = useState(true);
  const [time, setTime] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [speakReplies, setSpeakReplies] = useState(false);
  const [sessionContext, setSessionContext] = useState<any>(null);
  const [memories, setMemories] = useState<any[]>([]);

  // Post-session summary state
  const [showSummary, setShowSummary] = useState(false);
  const [summaryMemoryDiff, setSummaryMemoryDiff] = useState<any>(null);
  const [turnCount, setTurnCount] = useState(0);
  const summaryFired = useRef(false);

  const sessionMode = localStorage.getItem("aura_mode") || "free_conversation";

  // Fetch user profile + memory on mount
  useEffect(() => {
    async function fetchContext() {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;
      const userId = session?.user?.id;
      if (userId) {
        const { data: userProfile } = await supabase.from("users").select("*").eq("id", userId).maybeSingle();
        const { data: memDoc } = await supabase.from("memory_documents").select("*").eq("user_id", userId).maybeSingle();
        setSessionContext({
          profile: userProfile,
          memory: memDoc?.memory_json,
          mode: sessionMode,
          auth_token: session.access_token,
        });
      } else {
        setSessionContext({ profile: null, memory: null, mode: sessionMode });
      }
    }
    fetchContext();
  }, [sessionMode]);

  const {
    isConnected,
    isGeminiReady,
    isSpeaking: serverSpeaking,
    isUserSpeaking,
    isProcessing,
    isMemorizing,
    memorySaved,
    savedMemory,
    isGeminiDropped,
    userNeedsCorrection,
    error,
    transcripts,
    activeCard,
    mockResult,
    startAudioCapture,
    stopAudioCapture,
    endSession,
    sendText,
  } = useAudioStream(
    getBackendUrl().replace(/^http/, 'ws'),
    sessionContext, 
    hasStarted
  );

  // Real memory node count — from actual saved data, not a cosmetic counter
  const memoryNodeCount = savedMemory
    ? (savedMemory.mastered_vocabulary?.length || 0) +
      (savedMemory.failed_vocabulary?.length || 0) +
      (savedMemory.personal_context?.length || 0)
    : memories.length;

  // Start/stop audio capture
  useEffect(() => {
    if (!isGeminiReady) return;
    if (!muted) {
      startAudioCapture();
    } else {
      stopAudioCapture();
    }
    return () => stopAudioCapture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGeminiReady, muted]);

  // Session timer
  useEffect(() => {
    if (!hasStarted || isGeminiDropped || isMemorizing || showSummary) return;
    const t = setInterval(() => setTime((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [hasStarted, isGeminiDropped, isMemorizing, showSummary]);

  // Navigation guard — prevents closing the tab while a session is live or memory is saving
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isConnected || isMemorizing) {
        e.preventDefault();
        // Modern browsers show their own dialog; we just need to set returnValue
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isConnected, isMemorizing]);

  // If Gemini auto-dropped, close the end-confirmation modal in case it was open
  useEffect(() => {
    if (isGeminiDropped) setEndOpen(false);
  }, [isGeminiDropped]);

  // Count conversation turns (user turns)
  useEffect(() => {
    const userTurns = transcripts.filter((t) => t.role === "user").length;
    setTurnCount(userTurns);
  }, [transcripts]);

  // Visual memory capture — populate side-panel list from real saved memory data
  useEffect(() => {
    if (memorySaved && savedMemory) {
      const nodes: { id: number; label: string; cefr: string }[] = [];
      const cefr = savedMemory.cefr_level || sessionContext?.memory?.cefr_level || "A1";

      (savedMemory.mastered_vocabulary || []).slice(0, 3).forEach((v: any) => {
        nodes.push({ id: Date.now() + Math.random(), label: `✓ "${v.term}" mastered`, cefr });
      });
      (savedMemory.failed_vocabulary || []).slice(0, 2).forEach((v: any) => {
        nodes.push({ id: Date.now() + Math.random(), label: `↻ "${v.term}" needs practice`, cefr });
      });
      (savedMemory.personal_context || []).slice(0, 1).forEach((p: any) => {
        nodes.push({ id: Date.now() + Math.random(), label: `👤 ${p.fact}`, cefr });
      });

      if (nodes.length === 0) {
        nodes.push({ id: Date.now(), label: "Session insights captured", cefr });
      }

      setMemories(nodes);
    } else if (memorySaved && !savedMemory) {
      // Fallback: memory saved but no payload (short session / error path)
      setMemories([{ id: Date.now(), label: "Session recorded", cefr: "A1" }]);
    }
  }, [memorySaved, savedMemory, sessionContext]);

  // Show post-session summary after memory is saved (only once per session)
  useEffect(() => {
    if (!memorySaved || summaryFired.current) return;
    summaryFired.current = true;

    // Fetch the fresh memory doc to build the diff
    async function fetchFreshMemory() {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) {
        setShowSummary(true);
        return;
      }
      const { data: memDoc } = await supabase
        .from("memory_documents")
        .select("memory_json")
        .eq("user_id", userId)
        .maybeSingle();

      const mem = memDoc?.memory_json || {};
      const previousMemory = sessionContext?.memory || {};
      const previousMastered = new Set((previousMemory.mastered_vocabulary || []).map(memoryTermKey));
      const previousFailed = new Set((previousMemory.failed_vocabulary || []).map(memoryTermKey));
      const previousContext = new Set((previousMemory.personal_context || []).map(memoryFactKey));

      setSummaryMemoryDiff({
        newMastered: (mem.mastered_vocabulary || []).filter((v: any) => !previousMastered.has(memoryTermKey(v))).slice(-4),
        newFailed: (mem.failed_vocabulary || []).filter((v: any) => !previousFailed.has(memoryTermKey(v))).slice(-4),
        newContext: (mem.personal_context || []).filter((p: any) => !previousContext.has(memoryFactKey(p))).slice(-2),
        cefrLevel: mem.cefr_level || "A1",
        nextFocus: mem.next_focus || "",
        strengths: mem.strengths || [],
      });
      setShowSummary(true);
    }

    fetchFreshMemory();
  }, [memorySaved, sessionContext]);

  // Timer display
  const mm = String(Math.floor(time / 60)).padStart(2, "0");
  const ss = String(time % 60).padStart(2, "0");

  // Captions
  const lastTranscript = transcripts[transcripts.length - 1];
  let curWho = "none";
  let curCaption = "";
  if (lastTranscript) {
    curWho = lastTranscript.role === "aura" ? "aura" : "you";
    curCaption = lastTranscript.text;
  }

  // Correction extraction — replaces the hardcoded { was: "...", now: "..." }
  const correction = userNeedsCorrection ? extractCorrection(transcripts) : null;

  const modeLabel = sessionMode.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const userInitial = sessionContext?.profile?.display_name?.[0]?.toUpperCase()
    || sessionContext?.profile?.target_language?.[0]?.toUpperCase()
    || "Y";

  const targetLanguage = sessionContext?.profile?.target_language || "your language";

  return (
    <div className="relative min-h-screen bg-base text-ink overflow-hidden" data-testid="page-live-session">
      {/* Ambient glow */}
      <div aria-hidden className="pointer-events-none fixed -top-40 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full bg-aura-cyan/8 blur-[160px]" />
      <div aria-hidden className="pointer-events-none fixed -bottom-40 -right-40 w-[700px] h-[700px] rounded-full bg-aura-violet/12 blur-[160px]" />

      {/* ── Post-Session Summary overlay ── */}
      <PostSessionSummary
        visible={showSummary && sessionMode !== "mock_test"}
        sessionSeconds={time}
        sessionMode={sessionMode}
        memoryDiff={summaryMemoryDiff}
        targetLanguage={targetLanguage}
        turnCount={turnCount}
        onDone={() => {
          setShowSummary(false);
          nav("/dashboard");
        }}
      />

      {/* ── Mock-test result overlay ── */}
      {mockResult && (() => {
        const r = formatMockReport(mockResult);
        return (
          <div className="fixed inset-0 z-[60] grid place-items-center bg-base/90 backdrop-blur-md p-6" data-testid="mock-result">
            <div className="w-full max-w-md rounded-3xl glass hairline p-7 text-center">
              <div className="section-label">Mock Test · A1 Sprechen</div>

              {/* Lead with readiness, not a numeric score (honest verdict by design). */}
              <div className={`mt-4 text-4xl font-display font-semibold ${r.passed ? "text-aura-cyan" : "text-red-400"}`} data-testid="mock-headline">
                {r.headline}
              </div>
              <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper">
                AI estimate · not an official score
              </div>

              {r.verdict && (
                <p className="mt-4 text-[13px] leading-relaxed text-ink" data-testid="mock-verdict">
                  {r.verdict}
                </p>
              )}

              <div className="mt-5 text-[12px] text-whisper">
                Estimated <span className="text-ink font-medium">{r.earned} / {r.totalMax}</span> points · pass mark 60%
              </div>

              <div className="mt-4 space-y-3 text-left">
                {r.teils.map((t) => (
                  <div key={t.label} data-testid={`mock-teil-${t.label.replace(/\s+/g, "").toLowerCase()}`}>
                    <div className="flex items-center justify-between text-[12px]">
                      <span className="uppercase tracking-wide text-whisper">{t.label}</span>
                      <span className="text-ink">{t.points} / {t.max}</span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-panel/10 overflow-hidden">
                      <div className="h-full rounded-full bg-aura-cyan" style={{ width: `${t.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              {r.recap.length > 0 && (
                <div className="mt-5 text-left" data-testid="mock-recap">
                  <div className="section-label">What to work on</div>
                  <ul className="mt-2 space-y-1 text-[12px] text-whisper list-disc list-inside">
                    {r.recap.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={() => nav("/dashboard")}
                className="btn-primary w-full justify-center text-[12px] mt-7"
                data-testid="mock-result-done"
              >
                Back to dashboard
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── Pre-session Start overlay ── */}
      <AnimatePresence>
        {!hasStarted && !showSummary && (
          <motion.div
            className="fixed inset-0 z-40 grid place-items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-base/60 backdrop-blur-md" />
            <motion.div
              initial={{ y: 20, scale: 0.95 }}
              animate={{ y: 0, scale: 1 }}
              className="relative w-full max-w-[420px] rounded-3xl glass edge-glow p-8 text-center"
            >
              <div className="section-label mb-3">Ready to practice?</div>
              <h3 className="font-display text-[26px] font-light tracking-tight text-ink mb-2">
                Begin your session
              </h3>
              <p className="text-whisper text-[14px] leading-relaxed mb-8">
                AURA will connect to your microphone and initiate the {modeLabel.toLowerCase()} sequence.
              </p>
              
              <button 
                onClick={() => setHasStarted(true)}
                className="w-full py-4 rounded-full bg-ink text-base font-medium tracking-wide transition-transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_20px_rgb(var(--accent-cyan-rgb)/0.3)]"
              >
                Start Session
              </button>
              
              <button
                onClick={() => nav("/dashboard")}
                className="mt-4 text-[13px] text-whisper hover:text-ink font-mono tracking-wide uppercase transition-colors"
              >
                Return to Dashboard
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top header */}
      <div className="relative z-10 px-4 sm:px-6 pt-4 pb-3 flex items-center justify-between gap-3 border-b border-panel/6">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/dashboard"
            onClick={(e) => {
              if (isMemorizing) {
                e.preventDefault(); // block navigation while saving
                return;
              }
              if (hasStarted) {
                endSession();
              }
            }}
            className="w-9 h-9 rounded-lg border border-panel/10 hover:border-aura-cyan/40 grid place-items-center text-whisper hover:text-ink"
            data-testid="session-back"
            aria-label="Back to dashboard"
          >
            <ChevronRight size={16} className="rotate-180" />
          </Link>

          <div className="flex items-center gap-2 rounded-full bg-panel/[0.04] border border-panel/10 px-3 py-1.5 backdrop-blur-md">
            {isConnected ? <Wifi size={12} className="text-emerald-400" /> : <WifiOff size={12} className="text-red-400" />}
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
              {isConnected ? "Connected" : "Connecting..."}
            </span>
          </div>

          <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-ink truncate">Live · {modeLabel}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[12px] tabular-nums text-ink">{mm}:{ss}</span>
          <span className="pill hidden sm:inline-flex">{isProcessing ? "Thinking..." : "Listening"}</span>
          <button onClick={() => setEndOpen(true)} className="hidden sm:inline-flex btn-ghost py-2 px-4 border-red-400/40 text-red-300" data-testid="session-end-top">
            end
          </button>
        </div>
      </div>

      {/* Main grid */}
      <div className="relative z-10 grid lg:grid-cols-12 gap-4 px-4 sm:px-6 py-5 pb-32">
        {/* Video tiles */}
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4" data-testid="session-tiles">
          {/* Tutor tile */}
          <div className="relative rounded-3xl border border-panel/8 bg-elev/60 overflow-hidden min-h-[360px]">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 55% at 50% 40%, rgb(var(--accent-cyan-rgb) / 0.18), transparent 70%)" }} />
            <div className="absolute top-3 left-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper">
              <span className={`w-1.5 h-1.5 rounded-full ${serverSpeaking ? "bg-aura-cyan shadow-[0_0_8px_cyan]" : "bg-aura-cyan/50"}`} /> AURA · tutor
            </div>
            <div className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">HD · {isConnected ? "Live" : "Wait"}</div>
            <div className="relative h-full grid place-items-center pt-12 pb-16">
              <AuraAvatar speaking={serverSpeaking} />
            </div>
            <div className="absolute bottom-2 inset-x-0"><LiveWave active={serverSpeaking} tint="cyan" /></div>
          </div>

          {/* User tile */}
          <div className="relative rounded-3xl border border-panel/8 bg-elev/60 overflow-hidden min-h-[360px]">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 55% at 50% 40%, rgb(var(--accent-violet-rgb) / 0.16), transparent 70%)" }} />
            <div className="absolute top-3 left-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper">
              <span className={`w-1.5 h-1.5 rounded-full ${isUserSpeaking ? "bg-aura-violet shadow-[0_0_8px_violet]" : "bg-aura-violet/50"}`} /> You · learner
            </div>
            <div className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">{muted ? "muted" : "live"}</div>
            <div className="relative h-full grid place-items-center pt-12 pb-16">
              {video ? (
                <YouAvatar speaking={!muted && isUserSpeaking} initial={userInitial} />
              ) : (
                <div className="text-whisper-muted font-mono text-[11px] tracking-[0.22em] uppercase">camera off</div>
              )}
            </div>
            <div className="absolute bottom-2 inset-x-0"><LiveWave active={!muted && isUserSpeaking} tint="violet" /></div>
          </div>

          {/* Captions strip */}
          <div className="sm:col-span-2 rounded-3xl border border-panel/8 bg-elev/60 p-5 min-h-[140px]" data-testid="session-captions">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
              Live caption · {curWho === "aura" ? "AURA" : curWho === "you" ? "You" : "Waiting"}
            </div>
            <AnimatePresence mode="wait">
              {curCaption && (
                <motion.div key={lastTranscript?.id || "empty"} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.35 }} className="mt-2 font-serif text-[20px] sm:text-[22px] leading-[1.3] text-ink">
                  {curCaption.replace(/\*(.*?)\*/g, "$1")}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Correction bubble — shows real extracted correction */}
            <AnimatePresence>
              {correction && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.35 }}
                  className="mt-3 inline-flex flex-wrap items-center gap-3 rounded-full border border-aura-cyan/30 bg-aura-cyan/[0.06] pl-1.5 pr-4 py-1.5"
                  data-testid="session-correction"
                >
                  <span className="flex items-center gap-1.5 rounded-full bg-aura-cyan text-[#02121A] px-3 py-1 font-mono text-[10px] tracking-[0.18em] uppercase">
                    <Wand2 size={11} /> fix
                  </span>
                  {correction.was !== "…" && (
                    <>
                      <span className="font-mono text-[11px] text-whisper line-through">{correction.was}</span>
                      <span className="text-whisper-muted">→</span>
                    </>
                  )}
                  <span className="font-mono text-[11px] text-ink">{correction.now}</span>
                  <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-aura-cyan">{correction.rule}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Side panel */}
        <div className="lg:col-span-4 space-y-4" data-testid="session-side">
          <SessionPlanCard compact />
          {/* Memory feed */}
          <div className="rounded-3xl glass edge-glow p-5">
            <div className="flex items-center justify-between">
              <div className="section-label">Saving live</div>
              <span className="pill text-[10px]">{isMemorizing ? "memorizing..." : "memory"}</span>
            </div>
            <div className="mt-4 space-y-2 min-h-[140px]" data-testid="session-memory-feed">
              <AnimatePresence>
                {memories.length === 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-whisper-muted text-[13px]">
                    Nothing yet. AURA is listening.
                  </motion.div>
                )}
                {memories.map((m) => (
                  <motion.div key={m.id} initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }} className="rounded-2xl node-card p-3">
                    <div className="relative z-10 flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-aura-cyan">
                      <Sparkles size={12} /> saved
                    </div>
                    <div className="relative z-10 mt-1.5 font-display text-[15px] text-ink leading-tight">{m.label}</div>
                    <div className="relative z-10 mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">{m.cefr}</div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Teil 3 picture card — image with a labelled fallback when the file is missing */}
          {activeCard && (
            <div className="rounded-3xl glass hairline p-5" data-testid="teil3-card">
              <div className="section-label">Bildkarte · Teil 3</div>
              <div className="mt-3 relative aspect-square w-full rounded-2xl overflow-hidden bg-white/5 grid place-items-center">
                <span className="absolute text-2xl font-bold text-ink/30 select-none">{activeCard.germanLabel}</span>
                <img
                  src={teil3ImageUrl(activeCard.imageFilename)}
                  alt={activeCard.germanLabel}
                  data-testid="teil3-card-image"
                  className="relative w-full h-full object-contain"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
              <div className="mt-3 text-center">
                <div className="text-base font-semibold text-ink">{activeCard.germanLabel}</div>
                <div className="text-xs text-ink/50">{activeCard.englishLabel}</div>
              </div>
            </div>
          )}

          {/* Mode + status */}
          <div className="rounded-3xl glass hairline p-5">
            <div className="section-label">Mode</div>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="session-scenes">
              <button className="pill text-[10px]">{modeLabel}</button>
            </div>
            <div className="mt-4 flex items-center justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
              <span>{error ? `Error: ${error}` : "System nominal"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* In-session text input — type to AURA alongside the mic */}
      {hasStarted && (
        <form
          className="fixed left-1/2 -translate-x-1/2 bottom-20 z-30 glass edge-glow rounded-full px-3 py-2 flex items-center gap-2 w-[min(92vw,440px)]"
          data-testid="session-text-bar"
          onSubmit={(e) => {
            e.preventDefault();
            const text = draft.trim();
            if (!text || !isGeminiReady) return;
            if (sendText(text, speakReplies)) setDraft("");
          }}
        >
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={isGeminiReady ? "Type to AURA…" : "Connecting…"}
            disabled={!isGeminiReady}
            data-testid="session-text-input"
            className="flex-1 bg-transparent px-3 py-1.5 text-sm text-ink placeholder:text-ink/40 outline-none disabled:opacity-40"
          />
          <button
            type="button"
            onClick={() => setSpeakReplies((v) => !v)}
            aria-pressed={speakReplies}
            title={speakReplies ? "AURA speaks replies" : "Text-only replies"}
            data-testid="session-speak-replies"
            className={`shrink-0 w-9 h-9 rounded-full grid place-items-center border transition-colors ${speakReplies ? "border-aura-cyan/60 text-aura-cyan bg-aura-cyan/10" : "border-panel/10 text-ink/60"}`}
          >
            {speakReplies ? <Volume2 size={15} /> : <VolumeX size={15} />}
          </button>
          <button
            type="submit"
            disabled={!draft.trim() || !isGeminiReady}
            data-testid="session-text-send"
            aria-label="Send"
            className="shrink-0 w-9 h-9 rounded-full grid place-items-center border border-panel/10 hover:border-aura-cyan/60 disabled:opacity-40"
          >
            <Send size={15} />
          </button>
        </form>
      )}

      {/* Bottom controls */}
      <div className="fixed left-1/2 -translate-x-1/2 bottom-5 z-30 glass edge-glow rounded-full px-3 py-2 flex items-center gap-2" data-testid="session-controls">
        <button onClick={() => setMuted((m) => !m)} aria-pressed={muted} className={`w-11 h-11 rounded-full grid place-items-center border hover:border-aura-cyan/60 transition-colors ${muted ? "border-red-400/40 bg-red-400/10" : "border-panel/10"}`} aria-label="Mute" data-testid="session-mute">
          {muted ? <MicOff size={15} className="text-red-300" /> : <Mic size={15} />}
        </button>
        <button onClick={() => setVideo((v) => !v)} className="w-11 h-11 rounded-full grid place-items-center border border-panel/10 hover:border-aura-cyan/60" aria-label="Video">
          {video ? <Video size={15} /> : <VideoOff size={15} className="text-red-300" />}
        </button>
        <span className="hidden sm:inline-block w-px h-6 bg-panel/10 mx-1" />
        <button onClick={() => setEndOpen(true)} className="w-11 h-11 rounded-full grid place-items-center border border-red-400/40 bg-red-500/10 text-red-300 hover:bg-red-500/15" aria-label="End" data-testid="session-end">
          <PhoneOff size={15} />
        </button>
      </div>

      {/* ── Memory-saving overlay — blocks UI + navigation while committing memory ── */}
      <AnimatePresence>
        {isMemorizing && (
          <motion.div
            className="fixed inset-0 z-50 grid place-items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="saving-memory-overlay"
          >
            {/* Blurred backdrop */}
            <div className="absolute inset-0 bg-base/80 backdrop-blur-xl" />

            {/* Ambient glow */}
            <div aria-hidden className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-aura-cyan/10 blur-[140px]" />

            <motion.div
              initial={{ y: 16, scale: 0.97 }}
              animate={{ y: 0, scale: 1 }}
              className="relative w-full max-w-[400px] rounded-3xl glass edge-glow p-8 text-center"
            >
              {/* Pulsing AURA orb */}
              <div className="flex justify-center mb-6">
                <div className="relative w-20 h-20 grid place-items-center">
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{ background: 'radial-gradient(closest-side, rgb(var(--accent-cyan-rgb) / 0.55), transparent 70%)' }}
                    animate={{ scale: [1, 1.12, 1], opacity: [0.55, 0.95, 0.55] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <motion.div
                    className="absolute inset-2 rounded-full border border-aura-cyan/30"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
                  />
                  <div className="relative w-10 h-10 rounded-full glass grid place-items-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-aura-cyan shadow-[0_0_14px_rgb(var(--accent-cyan-rgb)/0.9)]" />
                  </div>
                </div>
              </div>

              <div className="section-label mb-2">Cognitive Harvest</div>
              <h3 className="font-display text-[22px] font-light tracking-tight text-ink">
                She's saving what mattered.
              </h3>
              <p className="mt-2 text-whisper text-[13px] leading-relaxed">
                AURA is committing your session to long-term memory.<br />
                <span className="text-whisper-muted">Please don't close this tab.</span>
              </p>

              {/* Animated bar */}
              <div className="mt-6 h-px w-full bg-panel/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, rgb(var(--accent-cyan-rgb)), rgb(var(--accent-violet-rgb)))' }}
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* End-session confirmation modal */}
      <AnimatePresence>
        {endOpen && (
          <motion.div className="fixed inset-0 z-40 grid place-items-center px-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="session-end-modal">
            <div className="absolute inset-0 bg-base/70 backdrop-blur-md" onClick={() => setEndOpen(false)} />
            <motion.div initial={{ y: 20, scale: 0.98 }} animate={{ y: 0, scale: 1 }} className="relative w-full max-w-[480px] rounded-3xl glass edge-glow p-7">
              <div className="section-label">End session?</div>
              <h3 className="mt-3 font-display text-[24px] font-light tracking-tight text-ink">She'll save what mattered.</h3>
              <p className="mt-2 text-whisper text-[14px]">{memoryNodeCount > 0 ? memoryNodeCount : turnCount} {memoryNodeCount > 0 ? `memory node${memoryNodeCount === 1 ? "" : "s"} captured` : `turn${turnCount === 1 ? "" : "s"} recorded`} · {mm}:{ss} of practice.</p>
              <div className="mt-6 flex gap-3 justify-end">
                <button onClick={() => setEndOpen(false)} className="btn-ghost" data-testid="session-end-cancel">stay</button>
                <button
                  onClick={() => { endSession(); setEndOpen(false); /* summary will appear when memorySaved fires */ }}
                  className="btn-primary"
                  data-testid="session-end-confirm"
                >
                  save & end
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LiveSession;
